"use client";

import { useEffect, useState, type KeyboardEvent } from "react";
import { Check, X } from "lucide-react";
import { useConfigurator } from "@/store/configurator";
import { Fretboard } from "@/components/configurator/Fretboard";
import { ConfiguratorFooter } from "@/components/configurator/ConfiguratorFooter";
import { useConfiguratorPrice } from "@/hooks/useConfiguratorPrice";
import { noteNamesFor } from "@/lib/configurator/string-notes";
import { configuratorFinish, finishColorOption } from "@/lib/configurator/finish";
import { formatPrice } from "@/lib/utils";

// Light colors need a dark selection ring so the gold border is still visible
const LIGHT_COLOR_IDS = new Set(["silver", "white", "yellow", "lime", "gold"]);

interface Props { onNext: () => void; onBack: () => void; }

/**
 * What per-string colour costs, stated before the customer turns it on.
 *
 * The amount comes from the server quote's PER_STRING_COLOR surcharge — the
 * authoritative pricing rule — so this never invents a fee and never hides one.
 * When the surcharge is zero or not configured at all, no fee is added, and we
 * say so rather than implying a cost.
 * Nothing is shown until a quote exists, so we never guess.
 */
function PerStringPriceNote() {
  const { quote, status } = useConfiguratorPrice();
  if (status !== "ready" || !quote) return null;

  // Read the configured amount, applied or not, so the cost is known before
  // the switch is flipped.
  const surcharge =
    quote.availableSurcharges.find((s) => s.key === "PER_STRING_COLOR") ??
    quote.surcharges.find((s) => s.key === "PER_STRING_COLOR");
  const amount = surcharge?.amount ?? 0;

  return (
    <span
      className="text-xs font-normal"
      style={{ color: amount > 0 ? "#E8C547" : "#8A8A8A" }}
    >
      {amount > 0 ? `+${formatPrice(amount)} per set` : "No extra charge"}
    </span>
  );
}

/**
 * ColorControls — the single palette for the whole step. Context-aware:
 * with no string selected it sets the set/base color; with a string
 * selected (per-string mode) it paints that string.
 */
function ColorControls({
  activeIndex,
  onClearActive,
}: {
  activeIndex: number | null;
  onClearActive: () => void;
}) {
  const {
    state,
    referenceData,
    setColor,
    setStringColor,
    updateString,
    togglePerStringColor,
    resetStringColors,
  } = useConfigurator();

  // A line's palette is scoped per instrument (e.g. Green is not offered on
  // acoustic Aurora One), so keep only this instrument's swatches.
  const colors = (
    referenceData.lines.find((line) => line.slug === state.lineId)?.colors ?? []
  ).filter((color) => color.instrumentSlug === state.instrumentId);
  const category =
    referenceData.instruments.find((i) => i.slug === state.instrumentId)?.category ?? "electric";
  const finish = configuratorFinish(category, state.lineId);
  // "Multi" is a pre-mixed full pack we can't paint onto the board —
  // the per-string toggle covers that use case properly.
  const selectableColors = (() => {
    const base = colors.filter((c) => c.slug !== "multi" && c.slug !== "multi-color");
    // Surface the default finish swatch (e.g. Phos Bronze for acoustic
    // Ultra-Sheer) even when the line's own palette doesn't carry it.
    const finishSwatch = finishColorOption(finish, state.instrumentId ?? "");
    if (finishSwatch && !base.some((c) => c.slug === finishSwatch.slug)) {
      return [finishSwatch, ...base];
    }
    return base;
  })();
  const lineName =
    referenceData.lines.find((line) => line.slug === state.lineId)?.name ??
    "this line";
  const customizedCount = state.strings.filter((s) => s.colorId !== null).length;

  const notes = noteNamesFor(state.instrumentId, state.stringCount ?? state.strings.length);
  const activeStr = activeIndex !== null ? state.strings[activeIndex] : null;
  const activeNote = activeIndex !== null ? notes[activeIndex] ?? `String ${activeIndex + 1}` : null;
  const editingString = state.isPerStringColor && activeStr != null;

  // What the palette currently reflects: the active string's color when
  // editing one, the set/base color otherwise.
  const currentSlug = editingString
    ? activeStr.colorId ?? state.colorId
    : state.colorId;
  const currentName = colors.find((c) => c.slug === currentSlug)?.name ?? null;

  function pick(slug: string) {
    if (editingString) {
      setStringColor(activeStr.position, slug);
    } else {
      setColor(slug);
    }
  }

  function handleColorKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (!["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(event.key)) {
      return;
    }

    const group = event.currentTarget.closest("[data-color-group]");
    const options = Array.from(
      group?.querySelectorAll<HTMLButtonElement>("[data-color-option]") ?? []
    );
    const currentIndex = options.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    event.preventDefault();
    const direction = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
    const nextIndex = (currentIndex + direction + options.length) % options.length;
    options[nextIndex]?.focus();
    options[nextIndex]?.click();
  }

  return (
    <div
      className="rounded-md border p-4"
      style={{
        backgroundColor: "#141414",
        borderColor: state.isPerStringColor ? "#E8C54755" : "#2A2A2A",
      }}
    >
      {/* Header row. The switch carries a permanent visible label and its price
          is stated before it is turned on — it used to be an unlabelled toggle
          whose surcharge only appeared once you had already enabled it. */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "#C0C0C0" }}>
            {state.isPerStringColor ? "String Color" : "Set Color"}
          </p>
          <p className="text-xs" style={{ color: "#8A8A8A" }}>
            {state.isPerStringColor
              ? "Tap a string on the neck, then pick its color."
              : `${selectableColors.length} colors available for ${lineName}.`}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          <label
            htmlFor="per-string-color"
            className="text-right text-xs font-semibold leading-tight"
            style={{ color: "#C0C0C0" }}
          >
            Different color
            <br />
            per string
            <br />
            <PerStringPriceNote />
          </label>
          <button
            id="per-string-color"
            type="button"
            onClick={() => { togglePerStringColor(!state.isPerStringColor); onClearActive(); }}
            className="relative flex-shrink-0 w-12 h-7 rounded-full transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
            style={{ backgroundColor: state.isPerStringColor ? "#E8C547" : "#3A3A3A" }}
            role="switch"
            aria-checked={state.isPerStringColor}
          >
            <span
              className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-200"
              style={{ left: state.isPerStringColor ? "calc(100% - 1.5rem)" : "0.25rem" }}
            />
          </button>
        </div>
      </div>

      {/* In per-string mode with no string selected, the palette still edits the
          base colour. Say so, rather than leaving the customer to discover it. */}
      {state.isPerStringColor && !editingString && (
        <p
          className="mb-4 rounded-sm border px-3 py-2 text-xs"
          style={{ borderColor: "#2A2A2A", backgroundColor: "#101010", color: "#B8B8B8" }}
          role="status"
        >
          No string selected — a color picked here becomes the{" "}
          <strong style={{ color: "#E0E0E0" }}>base color</strong> for every string
          you haven&apos;t given its own. Tap a string on the neck above to paint
          just that one.
        </p>
      )}

      {/* Editing context — which string the palette is painting right now */}
      {editingString && (
        <div
          className="flex items-center justify-between gap-3 mb-4 px-3 py-2 rounded-sm border"
          style={{ backgroundColor: "#E8C54710", borderColor: "#E8C54740" }}
          role="status"
        >
          <p className="text-sm">
            <span className="font-bold" style={{ color: "#E8C547" }}>Editing {activeNote}</span>
            <span className="ml-2" style={{ color: "#C0C0C0" }}>
              {currentName ?? "no color yet"}
              {!activeStr.colorId && currentName ? " (base)" : ""}
            </span>
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            {activeStr.colorId && (
              <button
                type="button"
                onClick={() => updateString(activeStr.position, { colorId: null })}
                className="px-2.5 py-1.5 text-xs font-semibold rounded-sm border transition-colors hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                style={{ borderColor: "#E8C54755", color: "#E8C547", backgroundColor: "transparent" }}
              >
                Use base color
              </button>
            )}
            <button
              type="button"
              onClick={onClearActive}
              aria-label={`Done editing ${activeNote}`}
              className="flex h-7 w-7 items-center justify-center rounded-sm transition-colors hover:text-[#F5F5F5] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
              style={{ color: "#9A9A9A" }}
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        </div>
      )}

      {/* The one palette — paints the active string, or the whole set */}
      <div
        className="grid grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] gap-y-4 gap-x-2"
        role="radiogroup"
        aria-label={
          editingString
            ? `Color for the ${activeNote} string`
            : state.isPerStringColor
              ? "Base color for every string without its own"
              : "Color for the whole set"
        }
        data-color-group
      >
        {selectableColors.map((c) => {
          const selected = currentSlug === c.slug;
          const isLight = LIGHT_COLOR_IDS.has(c.slug);
          const swatch = c.swatchCss ?? c.hexCode ?? "#E84040";
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c.slug)}
              onKeyDown={handleColorKeyDown}
              title={c.name}
              aria-label={
                editingString
                  ? `${c.name} for the ${activeNote} string`
                  : state.isPerStringColor
                    ? `${c.name} as the base color`
                    : `${c.name} for the whole set`
              }
              aria-checked={selected}
              role="radio"
              data-color-option
              className="flex flex-col items-center gap-1.5 group transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547] rounded-sm"
            >
              <div
                className="w-10 h-10 rounded-full transition-all group-hover:scale-110 flex items-center justify-center"
                style={{
                  background: swatch,
                  outline: selected
                    ? `3px solid #E8C547`
                    : `2px solid ${isLight ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.12)"}`,
                  outlineOffset: selected ? "2px" : "1px",
                }}
              >
                {selected && (
                  <Check
                    size={14}
                    aria-hidden
                    style={{ color: isLight ? "#0A0A0A" : "#fff", filter: isLight ? "none" : "drop-shadow(0 0 2px #000)" }}
                  />
                )}
              </div>
              <span
                className="text-[11px] font-medium leading-tight text-center"
                style={{
                  color: selected ? "#F5F5F5" : "#C0C0C0",
                  textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                }}
              >
                {c.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Per-string mode: surcharge + quiet status below a divider */}
      {state.isPerStringColor && (
        <div
          className="flex items-center justify-between gap-3 mt-4 pt-3 border-t"
          style={{ borderColor: "#2A2A2A" }}
        >
          <p className="text-xs" role="status" style={{ color: "#8A8A8A" }}>
            {customizedCount > 0 ? (
              <>
                <span aria-hidden style={{ color: "#E8C547" }}>● </span>
                {customizedCount} string{customizedCount > 1 ? "s" : ""} customized
              </>
            ) : (
              `All ${state.strings.length} strings use the base color`
            )}
          </p>
          {/* Explicit, deliberate discard. Switching the mode off no longer
              throws these away — it keeps them so the toggle is reversible. */}
          {customizedCount > 0 && (
            <button
              type="button"
              onClick={() => { resetStringColors(); onClearActive(); }}
              className="rounded-sm border px-2.5 py-1.5 text-xs font-semibold transition-colors hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
              style={{ borderColor: "#2A2A2A", color: "#9A9A9A", backgroundColor: "transparent" }}
            >
              Reset all to base color
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ColorStep({ onNext, onBack }: Props) {
  const { state, referenceData, setColor } = useConfigurator();
  // Which string the palette is painting (per-string mode); lives here so
  // the board (selection surface) and palette (tool) share one context.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const line = referenceData.lines.find((l) => l.slug === state.lineId) ?? null;
  const lineName = line?.name ?? "this line";
  const instrument = referenceData.instruments.find((i) => i.slug === state.instrumentId);
  const instrumentName = instrument?.name ?? "guitar";

  // Smart default: single-finish lines (everything but Aurora One) have one
  // finish, so preselect it — including the Phos Bronze default for acoustic
  // Ultra-Sheer — so the user can move straight through. Still switchable.
  useEffect(() => {
    if (state.colorId || !line || line.slug === "aurora-one") return;
    const finish = configuratorFinish(instrument?.category, line.slug);
    setColor(finish.setColorSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.colorId, line?.slug, instrument?.category]);

  // Can proceed if:
  // - Uniform mode: a set-level color is selected
  // - Per-string mode: every string has an explicit color OR there is a set-level fallback
  const canProceed = state.isPerStringColor
    ? state.colorId !== null || state.strings.every((s) => s.colorId !== null)
    : !!state.colorId;

  return (
    <div>
      <h2
        className="text-2xl font-black mb-2"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Choose your color.
      </h2>
      <p className="text-sm mb-6" style={{ color: "#9A9A9A" }}>
        One color for the set, or mix them.
      </p>

      {/* Fretboard — always visible (steel strings until a color is picked) */}
      {state.strings.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#C0C0C0" }}>
            {instrumentName} · {lineName}
          </p>
          {state.isPerStringColor ? (
            <Fretboard
              mode="color"
              activeIndex={activeIndex}
              onSelectString={setActiveIndex}
            />
          ) : (
            <Fretboard mode="preview" />
          )}
        </div>
      )}

      {/* The single palette — below the board it paints */}
      <ColorControls activeIndex={activeIndex} onClearActive={() => setActiveIndex(null)} />

      <ConfiguratorFooter
        onBack={onBack}
        onNext={onNext}
        nextLabel="Review My Set"
        nextDisabled={!canProceed}
        nextHint="Choose a color to continue"
      />
    </div>
  );
}
